import useOutsideClick from 'hooks/useOutsideClick';
import { SsqImages } from 'images/SsqImages';
import { tc } from 'language/i18nkey';
import { InputAssist } from 'mainpage/toparea/srchinput/InputAssist';
import { useRcentSearch } from 'mainpage/toparea/srchinput/useRcentSearch';
import { SrchType, useZstdSrch } from 'mainpage/toparea/srchinput/useZstdSrch';
import queryString from 'query-string';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isLang } from 'services';
import { fonts } from 'style/fonts';
import styled from 'styled-components';
import { ssqutil } from 'util/ssqutil';
import { isMainPage } from 'util/urls';
import { BRKS } from 'util/variables';
import closeIcon from './icon-close.svg';
import { SrchDropDown } from './SrchDropDown';
import { ToggleLblBtn } from './ToggleLblBtn';
import { useHandleSrch } from './useHandleSrch';

type Props = {};
export const SrchInput = (props: Props) => {
  const { inputTmp: input, setInputTmp: setInput,  suppressAutoSearch, setSuppressAutoSearch } = useZstdSrch();
  const { handleSrch: doSearch } = useHandleSrch();
  const [, addRecent] = useRcentSearch();
  const handleSearch = (e) => {
    const newInput = (input || '').trim();
    doSearch(newInput);
    addRecent(newInput);
    setTimeout(() => {
      refInput.current?.blur();
    }, 100);
    setShowAssist(false);
  };
  const { search, pathname } = useLocation();
  const { setInput: setSrchInput, input: srchInput, isAiMode, setIsAiMode, srchType } = useZstdSrch();

  const [showAssist, setShowAssist] = useState(false);
  const refAssist = React.useRef<HTMLDivElement>(null);
  const refInput = React.useRef<HTMLInputElement>(null);
  const prevSrchTypeRef = useRef(srchType);

  useEffect(() => {
    setInput(srchInput);
  }, [srchInput]);

  useEffect(() => {
    const qs = queryString.parse(search);
    if (typeof qs.searchInput === 'string') {
      setSrchInput(qs.searchInput);
      setInput(qs.searchInput);
    }
    setShowAssist(false);
  }, [search]);

  useOutsideClick(
    [refAssist, refInput],
    () => {
      setShowAssist(false);
    },
    []
  );

  useEffect(() => {
    if (showAssist) return;
    if ((input || '').trim() !== (srchInput || '').trim()) {
      setSrchInput(input);
    }
  }, [showAssist]);

  // 검색 유형이 변경되었고 검색어가 있으면 자동으로 검색 실행
  // 메인 페이지에서는 최초 입력으로 간주하여 자동 검색하지 않음
  useEffect(() => {
    if (suppressAutoSearch) {
      prevSrchTypeRef.current = srchType;
      setSuppressAutoSearch(false);
      return;
    }

    if (!isMainPage(pathname) && prevSrchTypeRef.current !== srchType && input?.trim()) {
      doSearch(input.trim());
      addRecent(input.trim());
      setShowAssist(false);
    }
    prevSrchTypeRef.current = srchType;
  }, [srchType, pathname]);

  const handleSrchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    } else if (e.key === 'Escape') {
      refInput.current?.blur();
      setShowAssist(false);
    }
  };
  const showAiToggle = srchType === SrchType.ALL;
  return (
    <Body data-id='header-srch-input'>
      <SrchDropDown enableNavToBoard />
      <Input
        type='text'
        placeholder={tc('i18n_srch_input_placeholder')}
        value={input || ''}
        ref={refInput}
        onFocus={() => setShowAssist(true)}
        onKeyDown={handleSrchKeyDown}
        onChange={(e) => setInput(e.target.value)}
        autoComplete='off'
        spellCheck={false}
        name='search-input'
      />
      {input ? (
        <img
          className={`close-icon${showAiToggle ? ' with-toggle' : ''}`}
          src={closeIcon}
          onClick={() => {
            setInput('');
            refInput.current?.focus();
          }}
        />
      ) : (
        <div className={`close-icon${showAiToggle ? ' with-toggle' : ''}`} />
      )}
      {showAiToggle && (
        <ToggleLblBtn
          label={tc('i18n_srch_input_ai_mode')}
          isOn={isAiMode}
          onClick={() => setIsAiMode(!isAiMode)}
          style={{ marginRight: '2.4rem' }}
        />
      )}
      {showAssist && <InputAssist refAssist={refAssist} hideHiro />}
    </Body>
  );
};

const Body = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10rem;
  border: 1px solid #407edb;
  width: 70rem;
  flex-shrink: 0;
  
  & [data-id='input-assist-content'] {
    left: 14.4rem;
    right: auto;
    width: 52.6rem;
  }

  @media screen and (max-width: ${BRKS.tabletMax}) {
    width: 46rem;
    
    & [data-id='input-assist-content'] {
      left: 2.2rem;
      right: 2.2rem;
      width: auto;
    }
  }
  @media screen and (max-width: ${BRKS.mobileMax}) {
    width: 35rem;
  }

  & .close-icon {
    bottom: 0;
    right: 0;
    width: 24px;
    height: 24px;
    margin: auto 2.4rem auto auto;
  }

  /* AI 모드 토글이 렌더링될 때는 close-icon 우측 여백을 0.8rem으로 좁힌다. */
  & .close-icon.with-toggle {
    margin-right: 0.8rem;
  }
`;

const Input = styled.input`
  position: relative;
  //width: 30rem;
  flex-grow: 1;
  width: 1px;
  font-size: 2rem;
  line-height: 2.4rem;
  padding: 1.35rem 1.4rem 1.35rem 4rem;
  background-image: url(${SsqImages.header.icon.search()});
  background-position: center left 0.8rem;
  background-repeat: no-repeat;
  
  &:placeholder-shown {
    font-size: 1.2rem;
  }

  &::placeholder {
    ${fonts.regular};
    font-size: 1.2rem;
  }
  /* @media screen and (max-width: ${BRKS.tabletMax}) {
    width: 20rem;
  }
  @media screen and (max-width: ${BRKS.mobileMax}) {
    width: 10rem;
  } */
`;
